import { API_URL } from '../utils/config';
import mockLawyers from '@utils/mockLawyers'; // adjust import path as needed
export const API_BASE_URL = API_URL;

// Assuming your AssemblyAI API Key is stored securely (e.g., environment variable)
// DO NOT HARDCODE YOUR API KEY HERE IN PRODUCTION
const ASSEMBLYAI_API_KEY = 'ddd1b952ec05402588b3a13e5cb040f3'; // Replace with your actual key or config method
const ASSEMBLYAI_BASE_URL = 'https://api.assemblyai.com/v2';

// =====================================================
// Base helpers. Refactor rest of the file to use these.
// =====================================================
const apiRequest = async (endpoint, method = 'GET', body = null) => {
    try {
        const token = localStorage.getItem('token');
        const headers = {
            'Content-Type': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method,
            headers,
            credentials: 'include',
            ...(body && { body: JSON.stringify(body) })
        });

        if (!response.ok) {
            throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error(`Error making ${method} request to ${endpoint}: `, error);
        throw error;
    }
};

const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
});


export const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem("token");

    try {
        const response = await fetch(`${API_BASE_URL}/llama/upload_document`, {
            method: "POST",
            headers: token ? { "Authorization": `Bearer ${token}` } : {},
            credentials: "include",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Failed to upload file: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        console.error("Error uploading file:", error);
        throw error;
    }
};

// Modify downloadDocument to be more generic
const downloadDocument = async (endpoint, body = null) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST', // Keep POST as per backend route
        headers: getAuthHeaders(),
        credentials: 'include',
        ...(body && { body: JSON.stringify(body) }) // Send body if provided
    });

    if (!response.ok) {
        // Attempt to get error details from response body
        const errorText = await response.text();
        console.error("Download error response:", errorText);
        throw new Error(`API request failed with status ${response.status}. ${errorText}`);
    }

    // Extract filename from Content-Disposition header if possible
    const disposition = response.headers.get('content-disposition');
    let filename = 'downloaded_file'; // Default filename
    if (disposition && disposition.includes('attachment')) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
        }
    }


    const blob = await response.blob();
    // Return blob and filename
    return { blob, filename };
};

// Update downloadSourceDocument to use the new endpoint and pass fileId
export const downloadSourceDocument = async (fileId) => {
    // Pass fileId in the body as expected by the backend controller
    return await downloadDocument(`/research/download_document`, { file_id: fileId });
}

// Modify performDownload to accept filename
export const performDownload = (blob, filename = 'downloaded_file') => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename; // Use the provided filename
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
};


// =====================================================
// Llama related API endpoints
// =====================================================
export async function submitResearchQuery(query) {
    try {
        return await apiRequest('/research/submit_query', 'POST', query)
    } catch {
        return [];
    }

}

// Get research history for a user
export async function getResearchHistory(userId) {
    const url = userId
        ? `/api/research/queries?userId=${userId}`
        : '/api/research/queries';

    return apiRequest(url);
}

// Get results for a specific query
export async function getResearchResults(queryId) {
    return apiRequest(`/api/research/results/${queryId}`);
}

const handleResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API Error: ${response.status}`);
    }

    return response.json();
};

export const documentEmbeddingApi = {
    getDocuments: async () => {
        try {
            return await apiRequest('/llama/documents');
        } catch (error) {
            console.error('Failed to fetch documents:', error);
            return [];
        }
    },

    getAnalytics: async () => {
        try {
            return await apiRequest('/llama/analytics');
        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            return {};
        }
    },

    uploadDocument: async (file) => {
        try {
            return await uploadFile(file);
        } catch (error) {
            console.error('Error uploading document:', error);
            throw error;
        }
    },

    deleteDocument: async (documentId) => {
        try {
            return await apiRequest(`/llama/delete_document`, 'DELETE', {
                documentId: documentId
            });
        } catch (error) {
            console.error('Error deleting document:', error);
            throw error;
        }
    },

    getParsedDocument: async (fileId) => {
        try {
            return await apiRequest(`/llama/parsed_document/${fileId}`);
        } catch (error) {
            console.error(`Error fetching parsed document for ${fileId}:`, error);
            throw error;
        }
    },

    getDocumentChunks: async (documentHash) => {
        if (!documentHash) {
            throw new Error("Document Hash is required to fetch chunks.");
        }
        try {
            return await apiRequest(`/llama/document_chunks/${documentHash}`);
        } catch (error) {
            console.error(`Error fetching document chunks for hash ${documentHash}:`, error);
            throw error;
        }
    },

    retryEmbedding: async (documentId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/retry`, {
                method: 'POST',
            });

            return handleResponse(response);
        } catch (error) {
            console.error('Error retrying document embedding:', error);
        }
    },

    // Function to download the raw document as a blob from our backend proxy
    downloadRawDocumentBlob: async (fileId) => {
        if (!fileId) {
            throw new Error("File ID is required to download the document.");
        }
        try {
            const token = localStorage.getItem('token');
            const headers = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/llama/download_raw_document/${fileId}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                // Try to get error message if backend sent one as JSON (e.g., for 404)
                let errorMsg = `Failed to download file (status: ${response.status})`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.message || errorMsg;
                } catch (e) { /* Ignore if response wasn't JSON */ }
                throw new Error(errorMsg);
            }

            // Extract filename from Content-Disposition header
            const disposition = response.headers.get('content-disposition');
            let filename = 'downloaded_file'; // Default
            if (disposition) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();

            return { blob, filename }; // Return blob and filename

        } catch (error) {
            console.error(`Error downloading raw document for ${fileId}:`, error);
            throw error; // Rethrow
        }
    },

    chat: async (userQuery) => {
        if (!userQuery) {
            throw new Error("User query cannot be empty.");
        }
        try {
            // Use the base apiRequest helper
            // The backend expects the query in the body under the key 'query'
            return await apiRequest('/llama/chat', 'POST', { query: userQuery });
        } catch (error) {
            console.error('Error sending chat message:', error);
            // Rethrow or handle as appropriate for the UI
            throw error;
        }
    }
};

// ========================================================================================
// Research API
// ========================================================================================
export const researchApi = {
    getResearchResults: async (query) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/research?q=${encodeURIComponent(query)}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return handleResponse(response);
        } catch (error) {
            console.error('Error fetching research results:', error);
        }
    },
};

// ========================================================================================
// Document Editing API.
// ========================================================================================
export const documentEditingApi = {
    getDocumentForEditing: async (documentId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/edit`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return handleResponse(response);
        } catch (error) {
            console.error('Error fetching document for editing:', error);
        }
    },

    saveEditedDocument: async (documentId, content) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/edit`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ content }),
            });

            return handleResponse(response);
        } catch (error) {
            console.error('Error saving edited document:', error);
        }
    },
};


// ========================================================================================
// Contract Generation API endpoints.
// ========================================================================================
export const workflowApi = {
    newWorkflow: async (initialData = {}) => {
        return await apiRequest('/api/workflows', 'POST', initialData);
    },

    getWorkflowById: async (workflowId) => {
        return await apiRequest(`/api/workflows/${workflowId}`);
    },

    newInteraction: async (workflowId, text) => {
        return await apiRequest(`/api/workflows/${workflowId}`, 'PUT', { text });
    },

    getWorkflows: async () => {
        return await apiRequest('/api/workflows');
    },

    renameWorkflow: async (workflowId, title) => {
        return await apiRequest(`/api/workflows/${workflowId}/rename`, 'PATCH', { title });
    },

    deleteWorkflow: async (workflowId) => {
        return await apiRequest(`/api/workflows/${workflowId}`, 'DELETE');
    }
};

const compileDocument = async (endpoint, renderedText) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ renderedText: renderedText }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error("Compilation error response:", errorText);
        throw new Error(`API request failed with status ${response.status}. ${errorText}`);
    }

    const disposition = response.headers.get('content-disposition');
    let filename = 'compiled_document';
    if (disposition && disposition.includes('attachment')) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
        }
    }

    const blob = await response.blob();
    return { blob, filename };
};

export const compilationApi = {
    compileDOCX: async (renderedText) => {
        return await compileDocument(`/api/utils/compile_docx`, renderedText);
    },

    compilePDF: async (renderedText) => {
        return await compileDocument(`/api/utils/compile_pdf`, renderedText);
    }
};

// ========================================================================================
// Lawyer Correspondence API.
// ========================================================================================
export const lawyerCorrespondenceApi = {
    getCorrespondences: async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
        return { data: mockLawyers };
    },

    getCorrespondenceDetails: async (id) => {
        await new Promise(resolve => setTimeout(resolve, 500));
        const correspondence = mockLawyers.find(c => c.id === id);
        return { data: correspondence };
    },

    updateCorrespondenceStatus: async (id, status) => {
        await new Promise(resolve => setTimeout(resolve, 600));
        return {
            success: true,
            message: `Correspondence status updated to ${status}`,
            data: { id, status }
        };
    },

    // ✅ NEW METHOD
    getLawyers: async () => {
        await new Promise(resolve => setTimeout(resolve, 800));
        return { data: mockLawyers };
    }
};

// ========================================================================================
// Document Comparison API.
// ========================================================================================
export const documentComparisonApi = {
    compareDocuments: async (originalFile, modifiedFile) => {
        const formData = new FormData();
        formData.append('originalFile', originalFile);
        formData.append('modifiedFile', modifiedFile);

        const token = localStorage.getItem("token");
        try {
            const response = await fetch(`${API_BASE_URL}/api/compare_documents`, {
                method: "POST",
                headers: token ? { "Authorization": `Bearer ${token}` } : {},
                credentials: "include",
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Failed to upload file: ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error uploading files:", error);
            throw error;
        }
    },
}


// ========================================================================================
// Document Extraction API.
// ========================================================================================
export const documentExtractionApi = {
    extractDocument: async (originalFile) => {
        const formData = new FormData();
        formData.append('originalFile', originalFile);

        const token = localStorage.getItem("token");
        try {
            const response = await fetch(`${API_BASE_URL}/api/extract_document`, {
                method: "POST",
                headers: token ? { "Authorization": `Bearer ${token}` } : {},
                credentials: "include",
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Failed to upload file: ${response.statusText}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error uploading files:", error);
            throw error;
        }
    },
}

// ========================================================================================
// Will Generation API.
// ========================================================================================
export function postWill(data) {
    return `hello from createWill ${data}`;
}

// ========================================================================================
// AssemblyAI API Helpers
// ========================================================================================

const handleAssemblyAIResponse = async (response) => {
    // Helper to handle JSON parsing and errors for AssemblyAI calls
    if (!response.ok) {
        let errorData;
        try {
            errorData = await response.json();
        } catch (e) {
            errorData = { error: `HTTP error ${response.status}: ${response.statusText}` };
        }
        // Use the 'error' field from AssemblyAI's response if available
        throw new Error(errorData.error || `AssemblyAI API Error: ${response.status}`);
    }
    return response.json();
};

// ========================================================================================
// Translation Transcription API (Using AssemblyAI)
// ========================================================================================
export const transcriptionApi = {
    /**
     * Step 1: Uploads the local audio file to AssemblyAI's servers.
     * @param {File} file - The audio file object to upload.
     * @returns {Promise<{upload_url: string}>} - Object containing the accessible URL for the uploaded file.
     * @see https://www.assemblyai.com/docs/api-reference/files/upload
     */
    uploadAudioFile: async (file) => {
        if (!file) throw new Error("File object is required for upload.");

        try {
            // Directly use fetch for this specific AssemblyAI endpoint
            const response = await fetch(`${ASSEMBLYAI_BASE_URL}/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': ASSEMBLYAI_API_KEY,
                    // Content-Type is implicitly set by fetch when using FormData/Blob/File
                    // but AssemblyAI docs specify application/octet-stream for --data-binary.
                    // For direct file upload via fetch body, this works.
                },
                body: file, // Pass the File object directly as the body
            });

            return handleAssemblyAIResponse(response); // Returns { upload_url: "..." }
        } catch (error) {
            console.error('Error uploading file to AssemblyAI:', error);
            throw error; // Rethrow to be caught by the calling function
        }
    },

    /**
     * Step 2: Submits the transcription job using the uploaded file's URL.
     * @param {string} audioUrl - The URL obtained from the uploadAudioFile step.
     * @returns {Promise<object>} - The initial transcript object (contains id, status, etc.).
     * @see https://www.assemblyai.com/docs/api-reference/transcripts/submit
     */
    submitTranscriptionJob: async (audioUrl) => {
        if (!audioUrl) throw new Error("Audio URL is required to submit transcription job.");

        try {
            const response = await fetch(`${ASSEMBLYAI_BASE_URL}/transcript`, {
                method: 'POST',
                headers: {
                    'Authorization': ASSEMBLYAI_API_KEY,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    audio_url: audioUrl,
                    // Add other parameters here if needed, e.g.:
                    // speaker_labels: true,
                    // language_code: 'en_us',
                }),
            });

            return handleAssemblyAIResponse(response); // Returns initial transcript object { id: "...", status: "queued", ... }
        } catch (error) {
            console.error('Error submitting transcription job to AssemblyAI:', error);
            throw error;
        }
    },

    /**
     * Step 3: Polls for the transcription result using the transcript ID.
     * @param {string} transcriptId - The ID obtained from the submitTranscriptionJob step.
     * @returns {Promise<object>} - The transcript object, including status and results when completed.
     * @see https://www.assemblyai.com/docs/api-reference/transcripts/get
     */
    getTranscriptionResult: async (transcriptId) => {
        if (!transcriptId) throw new Error("Transcript ID is required to get results.");

        try {
            const response = await fetch(`${ASSEMBLYAI_BASE_URL}/transcript/${transcriptId}`, {
                method: 'GET',
                headers: {
                    'Authorization': ASSEMBLYAI_API_KEY,
                    'Content-Type': 'application/json',
                },
            });

            return handleAssemblyAIResponse(response); // Returns transcript object { id: "...", status: "completed", text: "...", words: [...], ... } or { status: "processing" / "error", ...}
        } catch (error) {
            console.error(`Error fetching transcript result for ID ${transcriptId}:`, error);
            // Don't necessarily throw here, polling might retry. Return an error indicator.
            return { status: 'error', error: error.message || 'Failed to fetch status' };
        }
    },

    queryTranscription: async(query, transcription) => {
        const chatMessage = `We are working with the context of a transcription we just made that resulted in: ${transcription}
        The user input the following query regarding that transcription: ${query}
        Do your best with it.`;

        return await apiRequest('/api/chat', 'POST', { chatMessage });
    }
};